// src/components/forms/LoginForm.jsx
import React, { useEffect, useRef, useState } from 'react';
// Api
import client from '../../api/client';
// Routes / Constants
import {
  HOME_PAGE_URL,
} from '../../utils/Routes';
import { LOGIN_API } from '../../utils/ApiRoutes';
// Components
import LoadingSpinner from '../utils/LoadingSpinner';
// Context
import { useUser } from '../../context/UserContext';
// Hooks
import useNavigateToPage from '../../hooks/useNavigateToPage';
// Icons
import { IoEyeOutline, IoEyeOffOutline } from 'react-icons/io5';
import { setLocalToken, setSessionToken } from '../../utils/user/token';

function LoginForm() {
  const { setUser, setToken } = useUser();
  const navigateToPage = useNavigateToPage();

  const [loginInProgress, setLoginInProgress] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [loginFormData, setLoginFormData] = useState({
    email: '',
    password: '',
    keepMeLoggedIn: false,
  });

  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const alertRef = useRef(null);

  const [displayPassword, setDisplayPassword] = useState(false);
  const togglePasswordDisplay = () => setDisplayPassword((v) => !v);

  useEffect(() => {
    if (loginError) {
      const msg = String(loginError).toLowerCase();
      if (msg.includes('email') && emailRef.current) {
        emailRef.current.focus();
      } else if (msg.includes('password') && passwordRef.current) {
        passwordRef.current.focus();
      } else if (alertRef.current) {
        alertRef.current.focus();
      }
    }
  }, [loginError]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginInProgress(true);
    setLoginError(null);

    client
      .post(LOGIN_API, loginFormData, false)
      .then((res) => {
        const token = res?.data?.token;
        const existingUser = res?.data?.existingUser;
        setToken(token);
        setUser(existingUser);

        if (loginFormData.keepMeLoggedIn) {
          setLocalToken(token);
        } else {
          setSessionToken(token);
        }
        setLoginInProgress(false);
      })
      .then(() => navigateToPage(HOME_PAGE_URL))
      .catch((err) => {
        const apiMsg =
          err?.response?.data?.data?.message ||
          err?.response?.data?.message ||
          err?.response?.data?.reason ||
          err?.message ||
          'Unable to sign in with those details. Please try again.';
        setLoginError(apiMsg);
        setLoginInProgress(false);
      });
  };

  const handleChange = (event) => {
    setLoginError(null);
    const { name, value } = event.target;
    setLoginFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckedKeepMeLoggedIn = () => {
    setLoginFormData((prev) => ({
      ...prev,
      keepMeLoggedIn: !prev.keepMeLoggedIn,
    }));
  };

  return (
    <form
      className='grid gap-y-5'
      onSubmit={handleLogin}
      aria-describedby='login-help'
      aria-busy={loginInProgress ? 'true' : 'false'}
      noValidate
    >
      <p id='login-help' className='sr-only'>
        All fields are required. Use your account email and password.
      </p>

      {/* Email */}
      <div className='grid gap-y-2'>
        <label
          htmlFor='email'
          className='text-center text-sm font-medium text-colour8'
        >
          Email
        </label>
        <input
          ref={emailRef}
          type='email'
          name='email'
          id='email'
          className='w-full rounded bg-colour6 border border-colour7 border-solid text-colour1 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-colour6 focus:border-colour6 '
          placeholder='name@email.com'
          onChange={handleChange}
          value={loginFormData.email}
          required
          inputMode='email'
          autoComplete='email'
          autoCapitalize='none'
          spellCheck='false'
          aria-invalid={loginError ? 'true' : 'false'}
          aria-describedby={loginError ? 'email-error password-error' : 'email-hint'}
        />
        <span id='email-hint' className='sr-only'>
          Enter the email you used to sign up.
        </span>
        <span id='email-error' className='sr-only'>
          {loginError ? String(loginError) : ''}
        </span>
      </div>

      {/* Password */}
      <div className='grid gap-y-2'>
        <label
          htmlFor='password'
          className='text-center text-sm font-medium text-colour8'
        >
          Password
        </label>

        <div className='grid relative'>
          <input
            ref={passwordRef}
            type={displayPassword ? 'text' : 'password'}
            name='password'
            id='password'
            placeholder='••••••••'
            className='w-full rounded bg-colour6 border border-colour7 border-solid text-colour1 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-colour6 focus:border-colour6 '
            required
            onChange={handleChange}
            value={loginFormData.password}
            autoComplete='current-password'
            aria-invalid={loginError ? 'true' : 'false'}
            aria-describedby={loginError ? 'password-error email-error' : 'password-hint'}
          />
          <div className='absolute right-3 top-1/2 -translate-y-1/2'>
            <button
              type='button'
              onClick={togglePasswordDisplay}
              className='grid place-items-center text-colour5 text-2xl'
              aria-label={displayPassword ? 'Hide password' : 'Show password'}
            >
              {displayPassword ? <IoEyeOffOutline /> : <IoEyeOutline />}
            </button>
          </div>
        </div>

        <span id='password-hint' className='sr-only'>
          Password is required.
        </span>
        <span id='password-error' className='sr-only'>
          {loginError ? String(loginError) : ''}
        </span>
      </div>

      {/* Remember me + Links */}
      <div className='grid gap-4 justify-items-center'>
        <div className='grid grid-flow-col auto-cols-max gap-2 items-center'>
          <input
            className='h-4 w-4 border-colour8 rounded-sm cursor-pointer accent-colour5'
            type='checkbox'
            id='keepMeLoggedIn'
            name='keepMeLoggedIn'
            checked={loginFormData.keepMeLoggedIn}
            onChange={handleCheckedKeepMeLoggedIn}
          />
          <label className='text-colour8' htmlFor='keepMeLoggedIn'>
            Keep me logged in
          </label>
        </div>
      </div>

      {/* Submit */}
      <div className='grid'>
        <button
          type='submit'
          className='bg-colour5 text-colour1 py-2 rounded-2xl hover:brightness-110 active:scale-95 active:brightness-90 shadow-cardShadowBold disabled:opacity-60'
          disabled={loginInProgress}
        >
          {loginInProgress ? (
            <div className='grid grid-flow-col auto-cols-max gap-2 place-content-center place-items-center' aria-live='polite'>
              <LoadingSpinner xs={true} />
              <span className='sr-only'>Signing in…</span>
            </div>
          ) : (
            <span>Sign in</span>
          )}
        </button>
      </div>

      {/* Error */}
      {loginError && (
        <div
          ref={alertRef}
          role='alert'
          aria-live='assertive'
          aria-atomic='true'
          tabIndex={-1}
          className='text-error-red max-w-[22rem] mx-auto text-sm text-center'
        >
          {String(loginError)}
        </div>
      )}
    </form>
  );
}

export default LoginForm;
